import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/system';
import { FC } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetUserPasswordUsingPinDto } from 'src/api/userService/dto/SetUserPasswordUsingPin.dto';
import { Button } from 'src/components/button/button';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { Section } from 'src/components/section';
import { useSetPasswordUsingPin } from 'src/lib/hooks/use-set-password-using-pin';
import { usePasswordLoginSupabase } from 'src/auth/hooks/use-password-login-supabase';

import { createPasswordSchema } from './create-password-schema';
import { styles } from './styles';
import { SetPasswordFormValues } from './types';

export const DualOccupantLoginForm: FC<{ email: string; pin: string }> = ({ email, pin }) => {
    const { t } = useTranslation();

    const { mutateAsync, isPending } = useSetPasswordUsingPin();
    const { mutateAsync: loginSupabase } = usePasswordLoginSupabase();

    const methods = useForm<SetPasswordFormValues>({
        resolver: yupResolver(createPasswordSchema()) as Resolver<SetPasswordFormValues>,
        defaultValues: {
            email,
            password: '',
            repeatPassword: '',
        },
        mode: 'all',
    });

    const {
        control,
        handleSubmit,
        formState: { isValid, isSubmitted, isSubmitting },
    } = methods;

    const onSubmit = async (data: SetPasswordFormValues) => {
        const setPasswordData = new SetUserPasswordUsingPinDto({
            email,
            password: data.password,
            pin,
        });

        await mutateAsync(setPasswordData, {
            onError: (e) => {
                throw e;
            },
            onSuccess: async () => {
                await loginSupabase({
                    email,
                    password: data.password,
                });
            },
        });
    };

    const buttonIsLoading = isPending || isSubmitted || isSubmitting;

    return (
        <Section header={t('occupant_set_password')} style={styles.wrapper}>
            <ControlledField
                type="email"
                name="email"
                control={control}
                label={t('your_details_form_email_label')}
                placeholder={t('your_details_form_email_placeholder')}
                disabled
            />
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Password}
                    name="password"
                    control={control}
                    label={t('your_details_form_create_password_label')}
                    placeholder={t('your_details_form_create_password_placeholder')}
                />
                <ControlledField
                    fieldType={FieldTypes.Password}
                    name="repeatPassword"
                    control={control}
                    label={t('your_details_form_repeat_password_label')}
                    placeholder={t('your_details_form_repeat_password_placeholder')}
                />
            </Box>
            <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                css={styles.cta}
                loading={buttonIsLoading}
                disabled={!isValid}
            >
                {t('occupant_set_password_cta')}
            </Button>
        </Section>
    );
};
